// =============================================================================
// GENERATED by scripts/build-shared.cjs from functions/src/shared/jobCost.js
// DO NOT EDIT — edit the canonical file and re-run `node scripts/build-shared.cjs`.
// (TASK-9878 S2: ONE job-cost rule — BI → Profitability's labor cost and its
// Customer / Job / Project drill-down, and the Claude connector's get_job_cost
// run the same code.)
//
// Side-effect module (UMD): attaches `window.JobCost`; Node tests get it from
// require(). bi.js imports it once and reads window.JobCost.
//
// The body below is the canonical file's PORTABLE CORE, copied byte for byte.
// =============================================================================
(function (root) {
  'use strict';

  // ============================================================================
  // LABOR COST RATE — the one resolver for every "hours × rate" site in BI
  // ============================================================================
  // TASK-1481 follow-on (the reporting half — NOT the privacy half).
  //
  // THE BUG THIS FIXES. Seven sites built their personId→rate map straight off
  // the RAW person doc: `people.forEach(p => { if (p.costRate) map[p.id] = ... })`.
  // `costRate` has no writer in the product today — the Roster input
  // (`#person-cost-rate`) is force-hidden in both the add and edit paths and its
  // value is never read on save; HR writes `hrHourlyRate` (now onto the
  // peopleComp sibling). Only seed scripts set `costRate`, and they set it from
  // the same number they use for `hrHourlyRate`.
  //
  // Measured live 2026-08-23, read-only, all 86 tenants / 459 people docs:
  // 26 carry `costRate`, and 20 of those 26 are `show-account` (sales demo).
  // ZERO on any real operating tenant. On TITS that meant the P&L labor line,
  // customer profitability, the accrued-wages liability and three CSV exports
  // all priced labor at exactly $0.00 — $318,116.62 of YTD-2026 labor on
  // 13,292.3 clock hours, reported as free.
  //
  // 🔴 THIS IS THE INTERNAL COST SIDE ONLY. It never touches what a customer is
  // charged. TITS bills the truck, never the person — man-hour / EMPLOYEE price
  // book lines carry no customer-facing cost and nothing here changes that.
  // billing.js does not import anything from this file.
  //
  // ─────────────────────────────────────────────────────────────────────────
  // THREE THINGS THIS RESOLVER GETS RIGHT THAT THE FIRST CUT DID NOT
  // ─────────────────────────────────────────────────────────────────────────
  //
  // 1. `costRate` WINS over `hrHourlyRate`, not the other way round.
  //    In every job-costing system these are two deliberately different
  //    numbers: what you PAY someone (a wage) versus what a job should be
  //    CHARGED for an hour of them (a burdened cost rate). `costRate` sitting
  //    alongside `hrHourlyRate` on the person doc is exactly that split, and it
  //    got orphaned when the Roster input was hidden. So a tenant who goes and
  //    sets a cost rate must have it win — that is the whole point of the
  //    field. Verified no-op on today's data: of 459 people docs across 86
  //    tenants, 26 carry `costRate`, all 26 also carry `hrHourlyRate`, and the
  //    two values DISAGREE on exactly ZERO of them (measured 2026-08-23). The
  //    inversion changes no number anywhere today; it changes the default going
  //    forward, which is the part that matters.
  //
  // 2. A raw wage is not a job cost. Standard practice is a FULLY BURDENED
  //    rate — wage plus employer payroll taxes, workers' comp, benefits, PTO.
  //    Typical burden runs 30–60% over base and 30–45% for field labor;
  //    trucking sits at the high end because workers' comp is expensive. See
  //    `resolveBurden` for the multiplier and, more importantly, for why its
  //    DEFAULT is 1.0 and not a guess.
  //
  // 3. Salaried people used to price at exactly $0 — 2,038.3 of TITS's 13,292.3
  //    YTD hours, 15.3% of all hours worked, free. See `salaryHourlyRate` and
  //    `jobCostRateForEntry` for the rule: salary is allocated by whether the
  //    HOURS WERE CLOCKED TO A JOB, never by the person's title or ownership.
  //
  // ─────────────────────────────────────────────────────────────────────────
  // TWO KINDS OF SURFACE, TWO DIFFERENT NUMBERS — do not mix them up
  // ─────────────────────────────────────────────────────────────────────────
  //   JOB-COST surfaces (P&L COGS labor, customer/job/project profitability,
  //   the expense-report CSV) ask "what did this work cost the company?" They
  //   use `buildJobCostRates` + `jobCostForEntry`: burdened, and salary is
  //   allocated to job-clocked hours.
  //
  //   WAGE surfaces (Balance Sheet accrued wages, the payroll journal-entry
  //   export, the bank-feed export, the 1099 and contractor-payment exports)
  //   ask "what do we OWE / what did we PAY this person?" They use
  //   `buildPersonRateMap`: raw wage, unburdened, hourly-only. 🔴 Burden is
  //   employer-side cost, NOT money owed to the worker — burdening a 1099
  //   "Total Paid YTD" would put a wrong number on an IRS filing, and burdening
  //   accrued wages would overstate the wage liability. Allocated salary is not
  //   a payment either. Those five surfaces stay on the wage map on purpose.
  //
  // Pure core (no Firebase, no DOM). TASK-9878 S2: moved here VERBATIM from
  // public/src/views/bi.js (_biLaborRateCoreFactory) so BI and the connector run
  // ONE copy; tests/bi-labor-rate.unit.cjs extracts it from THIS file.
  function jcLaborRateCoreFactory() {
    'use strict';

    // Tenant-tunable, both under `biConfig` on tenants/{id}/settings/general —
    // the same doc the maintenance-reserve percentages already live in.
    const DEFAULTS = {
      // 🔴 1.0 = NO burden applied. Read resolveBurden() before changing this.
      burdenMultiplier: 1,
      maxBurdenPct: 200,
      // Annual salary ÷ productive hours. 2080 = 40h × 52w, the nominal figure
      // and the conservative one (a bigger divisor makes a smaller hourly cost).
      // A tenant that wants the PTO/holiday-adjusted ~1,880 sets it explicitly.
      salaryProductiveHours: 2080,
      minSalaryHours: 500,
      maxSalaryHours: 8760,
    };

    function _positive(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    function _entryHours(entry) {
      // Same idiom as every call site it replaces: totalHours, else hours.
      return _positive(entry && (entry.totalHours || entry.hours));
    }

    // WAGE / recorded-cost rate on a person ALREADY passed through
    // PersonComp.effective(). Resolution order:
    //   costRate (deliberate, tenant-set job-cost rate) → hrHourlyRate (wage).
    //
    // 🔴 costRate FIRST is intentional and is a change from the first cut. A
    // tenant who sets a cost rate is saying "cost this person's hours at THIS,
    // not at their wage" — that is what the field is for. The wage is the
    // fallback for the (currently universal) case where nobody set one.
    // No-op on live data today: 0 of the 26 docs carrying both fields disagree.
    //
    // Salary is deliberately NOT resolved here — see salaryHourlyRate.
    function laborRate(person) {
      if (!person) return 0;
      return _positive(person.costRate) || _positive(person.hrHourlyRate);
    }

    // Salaried job-cost rate: annual salary ÷ expected productive hours.
    // Returns 0 for anyone who is not explicitly salaried or has no salary on
    // file — never guesses a salary from anything else.
    function salaryHourlyRate(person, productiveHours) {
      if (!person) return 0;
      if (String(person.hrEmploymentType || '') !== 'salary') return 0;
      const salary = _positive(person.hrAnnualSalary);
      if (!salary) return 0;
      const hours = _positive(productiveHours) || DEFAULTS.salaryProductiveHours;
      return salary / hours;
    }

    // ── THE BURDEN DEFAULT: 1.0, i.e. OFF until a tenant opts in ─────────────
    // A fully burdened rate is the right way to cost labor, and this resolver
    // supports one. It does NOT default to one.
    //
    // The bug being fixed here was a silent wrong number ($0). Replacing it
    // with a different silent wrong number — every tenant's P&L quietly
    // inflated 35% on an industry average nobody in the company chose — is the
    // same failure wearing better clothes. Three concrete reasons:
    //   • A wage-based labor cost RECONCILES. An owner can tie $318,116.62 to
    //     payroll registers. A burdened figure ties to nothing they can check,
    //     and the first time it disagrees with their accountant they stop
    //     trusting the whole BI tab.
    //   • Real burden is 20% for a one-man LLC with no comp policy and 60% for
    //     a union shop with full benefits. The spread is wider than the number.
    //     A single guess is wrong for nearly everybody.
    //   • Burden is already partly double-counted: employer taxes, benefits and
    //     insurance a tenant enters as cost entries land in OpEx today. Adding a
    //     blanket multiplier on top books some of that cost twice.
    // So: opt-in, one number, tenant-set, and the P&L SAYS which basis it is on
    // (see the labor-basis footnote under COGS) rather than staying quiet. An
    // un-set tenant gets the wage — understated, but understated in a way that
    // is labelled and reconcilable, not invented.
    function resolveBurden(biConfig) {
      const pct = parseFloat(biConfig && biConfig.laborBurdenPct);
      if (!Number.isFinite(pct) || pct <= 0) {
        return { multiplier: DEFAULTS.burdenMultiplier, pct: 0, isDefault: true };
      }
      const clamped = Math.min(pct, DEFAULTS.maxBurdenPct);
      return { multiplier: 1 + (clamped / 100), pct: clamped, isDefault: false };
    }

    function resolveSalaryHours(biConfig) {
      const h = parseFloat(biConfig && biConfig.salaryProductiveHours);
      return (Number.isFinite(h) && h >= DEFAULTS.minSalaryHours && h <= DEFAULTS.maxSalaryHours)
        ? h : DEFAULTS.salaryProductiveHours;
    }

    // personId → RAW hourly wage. Unburdened, hourly-only, no salary.
    // This is the WAGE map: accrued wages, payroll JE, bank feed, 1099,
    // contractor payments. `resolve` is the comp overlay (_biComp); omitted or
    // identity is safe — it just means PersonComp has not loaded yet and the
    // legacy person-doc fields answer, exactly as before.
    function buildPersonRateMap(people, resolve) {
      const map = {};
      const overlay = typeof resolve === 'function' ? resolve : function (p) { return p; };
      (Array.isArray(people) ? people : []).forEach(function (raw) {
        if (!raw || !raw.id) return;
        const rate = laborRate(overlay(raw));
        if (rate > 0) map[raw.id] = rate;
      });
      return map;
    }

    // The JOB-COST rate set: two maps plus the resolved knobs. Both maps are
    // already burdened, so a call site never multiplies by the burden itself.
    // A person lands in exactly one of them — an hourly rate always wins, so
    // nobody is ever costed twice.
    //
    // 🔴 DENIED ≠ ABSENT, at the AGGREGATE. `compUnknown` already exists per-person
    // (TASK-1481) and the people cards honour it, but the P&L rolled everyone up
    // and a denied read looked exactly like a $0 wage. That is the SAME silent-$0
    // bug this whole change set exists to kill, one level up: comp now lives only
    // in `peopleComp` (measured 2026-08-23 — 0 of 35 TITS person docs still carry
    // a comp field, all 29 are in the sibling), and firestore.rules grants that
    // read to admin / workforce / billing / hr / hr-directory — NOT to `bi`. So a
    // BI-module manager with none of those gets an empty rate map and a P&L that
    // reports full revenue against zero labor. Count them here so the statement
    // can SAY SO instead of quietly inflating gross margin.
    function buildJobCostRates(people, resolve, biConfig, unknown) {
      const burden = resolveBurden(biConfig);
      const salaryHours = resolveSalaryHours(biConfig);
      const overlay = typeof resolve === 'function' ? resolve : function (p) { return p; };
      const isUnknown = typeof unknown === 'function' ? unknown : function () { return false; };
      const hourly = {};
      const salary = {};
      const compDenied = {};
      (Array.isArray(people) ? people : []).forEach(function (raw) {
        if (!raw || !raw.id) return;
        const person = overlay(raw);
        const wage = laborRate(person);
        if (wage > 0) { hourly[raw.id] = wage * burden.multiplier; return; }
        const sal = salaryHourlyRate(person, salaryHours);
        if (sal > 0) { salary[raw.id] = sal * burden.multiplier; return; }
        // Zero rate AND the viewer was denied the comp read — not a free person.
        if (isUnknown(raw)) compDenied[raw.id] = true;
      });
      return { hourly, salary, burden, salaryHours, compDenied };
    }

    // How many people with hours IN THIS PERIOD had their comp denied, and how
    // many of those hours are therefore missing from COGS. Counted over the
    // period's own clock entries, not the whole roster — a denied office worker
    // who clocked nothing this month is not a hole in this month's statement.
    function compDeniedImpact(entries, rates) {
      const out = { people: 0, hours: 0 };
      if (!rates || !rates.compDenied) return out;
      const seen = {};
      (Array.isArray(entries) ? entries : []).forEach(function (e) {
        if (!e || !e.personId || !rates.compDenied[e.personId]) return;
        if (!seen[e.personId]) { seen[e.personId] = true; out.people += 1; }
        out.hours += _entryHours(e);
      });
      return out;
    }

    // Burdened cost rate for ONE clock entry.
    //
    // 🔴 THE SALARY RULE. A salaried person's hours are costed to a job when —
    // and only when — the entry is CLOCKED TO A JOB (`entry.jobId`). Not by
    // title, not by role, not by whether they own the company. An earlier cut
    // of this tried to split on owner-vs-employee; that needs a cap table BI
    // does not have, gets the facts wrong, and answers the wrong question
    // anyway. A job-clocked hour is direct labor no matter whose hour it is; an
    // unassigned hour is overhead no matter whose hour it is. It is mechanical,
    // it reads one field, and it matches how the tenant already tracks the work.
    //
    // On TITS: 808.5 of the 2,038.3 previously-free salaried hours are clocked
    // to a job (Sean 532.5, Cassian 165.1, Chris 80.9) and start costing;
    // 1,229.8 unassigned hours stay out of COGS, where they belong.
    function jobCostRateForEntry(entry, rates) {
      if (!entry || !rates) return 0;
      const pid = entry.personId;
      if (!pid) return 0;
      const hourly = _positive(rates.hourly && rates.hourly[pid]);
      if (hourly > 0) return hourly;
      if (!entry.jobId) return 0;
      return _positive(rates.salary && rates.salary[pid]);
    }

    function jobCostForEntry(entry, rates) {
      const rate = jobCostRateForEntry(entry, rates);
      if (!(rate > 0)) return 0;
      return _entryHours(entry) * rate;
    }

    // Accrued-wages predicate for the Balance Sheet.
    //
    // 🔴 WHY THIS IS NOT `!entry.paid`. Nothing in the product ever writes
    // `paid` onto a clockEntry — measured live: 0 of 1,847 TITS clock entries
    // carry the key at all (`paid` exists on tickets and revenueEntries, never
    // on clock). `!c.paid` therefore treats EVERY shift ever worked as unpaid.
    // Today the $0 rate hides that; the moment the rate is real, the naive form
    // would book $318,116.62 of already-paid 2026 payroll as a current
    // liability and blow up the Balance Sheet. So: accrue only what the data can
    // actually support — an entry must EXPLICITLY say it is unpaid. With no
    // writer this keeps accrued wages at $0 (byte-identical to today) and starts
    // reporting correctly the moment payroll-payment tracking lands.
    function isUnpaidClockEntry(entry) {
      return !!entry && entry.paid === false;
    }

    return {
      DEFAULTS, laborRate, salaryHourlyRate, resolveBurden, resolveSalaryHours,
      buildPersonRateMap, buildJobCostRates, jobCostRateForEntry, jobCostForEntry,
      isUnpaidClockEntry, compDeniedImpact,
      // TASK-9878 S2 — the per-entry hours rule, exported so per-person hours
      // on the connector read entries exactly as the cost does.
      entryHours: _entryHours,
    };
  }


  var JC_LABOR = jcLaborRateCoreFactory();
  var JC_DIMS = ['customer', 'job', 'project'];
  var JC_UNASSIGNED = 'Unassigned';

  // ============================================================================
  // jcEntityTotals — who a dollar or an hour belongs to, per the P&L drill-down
  // ============================================================================
  // Lifted out of bi.js renderPLDrilldown (the Customer, Job and Project views),
  // rule for rule. Inputs are the drill-down's own inputs, already selected by
  // the P&L (computePLData): the period's revenue rows (accounting basis,
  // written-off rows out), the period's recognized cost entries expanded into
  // split lines (CostSplit), the period's clock entries and the job-cost rate
  // set (buildJobCostRates). Nothing here filters by date, basis or status.
  //
  //   customer  revenue  → r.customerId || r.customerName || 'Unassigned'
  //             cost     → c.customerId || the job's customer || 'Unassigned'
  //             labor    → the customer a revenue row in the period names for
  //                        that job (the last one wins), else the job's
  //                        customer, else 'Unassigned' (TASK-9512)
  //             jobs     → every job id seen, except on 'Unassigned'
  //   job       revenue / labor / cost → their own jobId; rows with none are
  //             not in the Job view. customerHint = the last revenue row's
  //             customerName for the job.
  //   project   the job's projectId; revenue, labor and cost through their
  //             jobId; jobs counted from revenue and labor only (as BI does).
  //
  // opts: { jobCustomerKey(jobId) -> key or '', jobProjectId(jobId) -> id or
  // null, labor (default JC_LABOR) }. Returns { rows, byKey }: rows in the order
  // BI first creates them (each { key, nameHint, customerHint, revenue, labor,
  // direct, directByCategory, hours, openShifts, jobs: [], people }), byKey a
  // null-prototype map. directByCategory = direct by cost category ('other' when
  // none), summing to direct.
  // `people` = per person id { hours, labor, entries } from the entity's clock
  // entries; `hours` = the labor core's entry-hours rule (totalHours, else
  // hours). Dollars are sums as BI sums them (no rounding here).
  // Ids are data: every keyed map is null-prototype ("constructor" is a key).
  function jcEntityTotals(dim, input, opts) {
    if (JC_DIMS.indexOf(dim) < 0) throw new Error('jcEntityTotals: unknown dimension ' + dim);
    input = input || {};
    opts = opts || {};
    var labor = opts.labor || JC_LABOR;
    var rates = input.rates || null;
    var jobCustomerKey = typeof opts.jobCustomerKey === 'function' ? opts.jobCustomerKey : function () { return ''; };
    var jobProjectId = typeof opts.jobProjectId === 'function' ? opts.jobProjectId : function () { return null; };
    var revenue = Array.isArray(input.revenue) ? input.revenue : [];
    var costViews = Array.isArray(input.costViews) ? input.costViews : [];
    var clock = Array.isArray(input.clock) ? input.clock : [];

    var byKey = Object.create(null);
    var rows = [];
    function row(key, nameHint) {
      var r = byKey[key];
      if (!r) {
        r = { key: key, nameHint: nameHint || '', customerHint: '', revenue: 0, labor: 0, direct: 0, hours: 0, openShifts: 0, _jobs: Object.create(null), jobs: [], people: Object.create(null), directByCategory: Object.create(null) };
        byKey[key] = r;
        rows.push(r);
      }
      return r;
    }
    function addJob(r, jobId) {
      if (jobId && !r._jobs[jobId]) { r._jobs[jobId] = true; r.jobs.push(jobId); }
    }
    function addDirect(r, c) {
      var amt = (c.amount || 0);
      var cat = c.category || 'other';
      r.direct += amt;
      r.directByCategory[cat] = (r.directByCategory[cat] || 0) + amt;
    }
    function addClock(r, c) {
      var cost = labor.jobCostForEntry(c, rates);
      var hrs = labor.entryHours(c);
      r.labor += cost;
      r.hours += hrs;
      var open = c.status === 'active' || (!c.clockOut && !(hrs > 0) && c.status !== 'completed');
      if (open) r.openShifts += 1;
      if (c.personId) {
        var p = r.people[c.personId] || (r.people[c.personId] = { hours: 0, labor: 0, entries: 0, openShifts: 0 });
        p.hours += hrs;
        p.labor += cost;
        p.entries += 1;
        if (open) p.openShifts += 1;
      }
    }

    if (dim === 'customer') {
      revenue.forEach(function (r) {
        var k = r.customerId || r.customerName || JC_UNASSIGNED;
        var x = row(k, r.customerName);
        x.revenue += (r.amount || 0);
        if (r.jobId) addJob(x, r.jobId);
      });
      costViews.forEach(function (c) {
        var k = c.customerId || jobCustomerKey(c.jobId) || JC_UNASSIGNED;
        var x = row(k);
        addDirect(x, c);
        if (c.jobId && k !== JC_UNASSIGNED) addJob(x, c.jobId);
      });
      var jobCustomerMap = Object.create(null);
      revenue.forEach(function (r) { if (r.jobId && (r.customerId || r.customerName)) jobCustomerMap[r.jobId] = r.customerId || r.customerName; });
      clock.forEach(function (c) {
        var k = (c.jobId && jobCustomerMap[c.jobId]) || jobCustomerKey(c.jobId) || JC_UNASSIGNED;
        var x = row(k);
        addClock(x, c);
        if (c.jobId && k !== JC_UNASSIGNED) addJob(x, c.jobId);
      });
    } else if (dim === 'job') {
      revenue.forEach(function (r) {
        if (!r.jobId) return;
        var x = row(r.jobId, r.jobName);
        x.revenue += (r.amount || 0);
        if (r.customerName) x.customerHint = r.customerName;
        addJob(x, r.jobId);
      });
      clock.forEach(function (c) {
        if (!c.jobId) return;
        var x = row(c.jobId, c.jobName || c.jobTitle);
        addClock(x, c);
        addJob(x, c.jobId);
      });
      costViews.forEach(function (c) {
        if (!c.jobId) return;
        var x = row(c.jobId, c.jobName);
        addDirect(x, c);
        addJob(x, c.jobId);
      });
    } else {
      revenue.forEach(function (r) {
        var pid = r.jobId ? jobProjectId(r.jobId) : null;
        if (!pid) return;
        var x = row(pid);
        x.revenue += (r.amount || 0);
        addJob(x, r.jobId);
      });
      clock.forEach(function (c) {
        var pid = c.jobId ? jobProjectId(c.jobId) : null;
        if (!pid) return;
        var x = row(pid);
        addClock(x, c);
        addJob(x, c.jobId);
      });
      costViews.forEach(function (c) {
        var pid = c.jobId ? jobProjectId(c.jobId) : null;
        if (!pid) return;
        addDirect(row(pid), c);
      });
    }
    rows.forEach(function (r) { delete r._jobs; });
    return { rows: rows, byKey: byKey };
  }

  // Profit and margin exactly as the drill-down prints them: profit = revenue −
  // labor − direct; margin % = profit ÷ revenue × 100, and 0 when revenue is 0.
  function jcProfit(revenue, labor, direct) {
    var profit = revenue - labor - direct;
    return { profit: profit, margin: revenue > 0 ? (profit / revenue * 100) : 0 };
  }

  // ============================================================================
  // TASK-9878 (Slice 3) — the drill-down's Crew Member and Vehicle views, and
  // its sort, for the connector's get_profit_rankings.
  // ============================================================================
  // Lifted rule for rule from bi.js renderPLDrilldown ('crew', 'vehicle' and the
  // "Sort" block). Since the TASK-9878 follow-up bi.js renders those two views
  // FROM these (the browser mirror's crewTotals / vehicleTotals / rank) — one
  // implementation; tests/task9878-bi-shared-crew-vehicle.unit.cjs proves the
  // switch-over prints the same table as bi.js's old inline copy did. Hours are
  // the labor core's entry-hours rule (totalHours, else hours, read as a number,
  // never negative) — the same rule the cost and the Customer / Job views use.
  // bi.js's old Crew view added the raw field, so a clock entry whose hours were
  // stored as TEXT ("4") was glued on as text (8 + "4" = "84") and crashed the
  // view; reading them here fixed that.

  // The Crew Member view. input: { revenue, clock, rates, people } — the
  // period's revenue rows, the period's clock entries, the job-cost rate set and
  // BI's people list (every person doc except noLongerEmployed). A person's
  // revenue is each job's period revenue split by their share of the hours
  // clocked to that job in the period. Rows: people with hours > 0, in BI's
  // row order (see below), each { personId, name, role, hours, labor, revAttr, perHr, profit,
  // openShifts }; profit = revAttr − labor, perHr = revAttr ÷ hours.
  function jcCrewTotals(input, opts) {
    input = input || {};
    opts = opts || {};
    var labor = opts.labor || JC_LABOR;
    var rates = input.rates || null;
    var revenue = Array.isArray(input.revenue) ? input.revenue : [];
    var clock = Array.isArray(input.clock) ? input.clock : [];
    var people = Array.isArray(input.people) ? input.people : [];
    var personJobs = Object.create(null);
    var jobTotalHours = Object.create(null);
    clock.forEach(function (c) {
      var hrs = labor.entryHours(c);
      if (c.jobId) jobTotalHours[c.jobId] = (jobTotalHours[c.jobId] || 0) + hrs;
      if (!c.personId || !c.jobId) return;
      var pj = personJobs[c.personId] || (personJobs[c.personId] = Object.create(null));
      pj[c.jobId] = (pj[c.jobId] || 0) + hrs;
    });
    var jobRevenue = Object.create(null);
    revenue.forEach(function (r) { if (r.jobId) jobRevenue[r.jobId] = (jobRevenue[r.jobId] || 0) + (r.amount || 0); });
    // Row order = key order of BI's plain-object map (integer-like ids first,
    // then insertion order) — it decides ties in the sort.
    var map = Object.create(null);
    function get(pid, name, role) {
      if (!map[pid]) map[pid] = { personId: pid, name: name, role: role, hours: 0, labor: 0, revAttr: 0, openShifts: 0 };
      return map[pid];
    }
    people.forEach(function (p) {
      if (!p || !p.id) return;
      // A repeated id takes the LAST doc's name and role (BI's map assignment).
      var x = get(p.id, p.name, '');
      x.name = p.name;
      x.role = p.crewType || p.role || (p.roles && p.roles[0]) || '';
    });
    clock.forEach(function (c) {
      if (!c.personId) return;
      var x = get(c.personId, c.personId, '');
      var hrs = labor.entryHours(c);
      x.hours += hrs;
      x.labor += labor.jobCostForEntry(c, rates);
      if (c.status === 'active' || (!c.clockOut && !(hrs > 0) && c.status !== 'completed')) x.openShifts += 1;
    });
    Object.keys(personJobs).forEach(function (pid) {
      var jobs = personJobs[pid];
      Object.keys(jobs).forEach(function (jobId) {
        var totalHrs = jobTotalHours[jobId] || 1;
        var rev = jobRevenue[jobId] || 0;
        if (map[pid]) map[pid].revAttr += (jobs[jobId] / totalHrs) * rev;
      });
    });
    var rows = [];
    Object.keys(map).forEach(function (pid) {
      var r = map[pid];
      if (!(r.hours > 0)) return;
      r.perHr = r.hours > 0 ? (r.revAttr / r.hours) : 0;
      r.profit = r.revAttr - r.labor;
      rows.push(r);
    });
    return rows;
  }

  // TASK-9592 rule (bi.js _withAllocationVehicle): a cost line with no vehicle of
  // its own (vehicleId / truckId / trailerId / assetId) whose truck or trailer
  // Cost Allocation names one takes that vehicle. allocById: id -> { type, refId }.
  // Returns a clone when it fills, the same object otherwise.
  function jcWithAllocationVehicle(c, allocById) {
    if (!c || c.vehicleId || c.truckId || c.trailerId || c.assetId || !c.costAllocationId) return c;
    var a = allocById && typeof allocById.get === 'function' ? allocById.get(c.costAllocationId) : null;
    if (!a || (a.type !== 'truck' && a.type !== 'trailer')) return c;
    var ref = a.refId == null ? '' : String(a.refId).trim();
    if (!ref) return c;
    var out = {};
    Object.keys(c).forEach(function (k) { out[k] = c[k]; });
    out.vehicleId = ref;
    out.vehicleType = c.vehicleType || a.type;
    out._vehicleFromAllocation = true;
    return out;
  }

  // The Vehicle view. input: { revenue, costViews, maint, jobDays, equipment,
  // periodDays, inPeriod } — the period's revenue rows, the period's cost lines
  // (split-aware, allocation vehicle filled), the period's maintenance records,
  // EVERY job-day (revenue follows a job to every vehicle ever put on it, split
  // evenly — BI reads all job-days, not the period's), every active truck,
  // trailer and asset (each { id, unit, name, type | _type, loan }), the
  // period's day count (bi.js getPeriodDays) and the period test for a job-day's
  // date (utilization). Rows: vehicles with revenue, costs, maintenance or a
  // loan, in BI's row order, each { vehicleId, name, type, revenue, costs,
  // loan, maint, net, util, days }; net = revenue − costs − maint (no labor —
  // the Vehicle view has none), util = job-days in the period ÷ period days × 100.
  // maintCost (TASK-10171 item 4): the caller passes PnlStatement.woCountedCost.
  function jcVehicleTotals(input) {
    input = input || {};
    var revenue = Array.isArray(input.revenue) ? input.revenue : [];
    var costViews = Array.isArray(input.costViews) ? input.costViews : [];
    var maint = Array.isArray(input.maint) ? input.maint : [];
    var jobDays = Array.isArray(input.jobDays) ? input.jobDays : [];
    var equipment = Array.isArray(input.equipment) ? input.equipment : [];
    var periodDays = Number(input.periodDays) || 0;
    var inPeriod = typeof input.inPeriod === 'function' ? input.inPeriod : function () { return false; };
    var maintCost = typeof input.maintCost === 'function' ? input.maintCost : function (m) { return (m.costs && m.costs.totalCost) || m.totalCost || 0; };
    function trucksOf(jd) {
      var t = jd && (jd.trucks || jd.assignedTrucks || []);
      return Array.isArray(t) ? t : [];
    }
    function idOf(t) { return t == null ? t : (t.id || t); }
    var jobDayVehicles = Object.create(null);
    jobDays.forEach(function (jd) {
      trucksOf(jd).forEach(function (t) {
        var id = idOf(t);
        if (!jobDayVehicles[jd.jobId]) jobDayVehicles[jd.jobId] = new Set();
        jobDayVehicles[jd.jobId].add(id);
      });
    });
    var map = Object.create(null); // key order decides ties, as BI's plain object does
    equipment.forEach(function (e) {
      if (!e || !e.id) return;
      map[e.id] = { vehicleId: e.id, name: e.unit || e.name || e.id, type: e.type || e._type, revenue: 0, costs: 0, loan: (e.loan && e.loan.enabled) ? (e.loan.monthlyPayment || 0) : 0, maint: 0 };
    });
    revenue.forEach(function (r) {
      if (!r.jobId) return;
      var vehicles = jobDayVehicles[r.jobId];
      if (!vehicles || vehicles.size === 0) return;
      var share = (r.amount || 0) / vehicles.size;
      vehicles.forEach(function (vid) { if (map[vid]) map[vid].revenue += share; });
    });
    costViews.forEach(function (c) {
      if (c.vehicleId && map[c.vehicleId]) map[c.vehicleId].costs += (c.amount || 0);
    });
    maint.forEach(function (m) {
      var vid = m.vehicleId || m.truckId;
      if (vid && map[vid]) map[vid].maint += maintCost(m);
    });
    var vehicleDays = Object.create(null);
    jobDays.forEach(function (jd) {
      if (!inPeriod(jd.date)) return;
      trucksOf(jd).forEach(function (t) {
        var id = idOf(t);
        vehicleDays[id] = (vehicleDays[id] || 0) + 1;
      });
    });
    var rows = [];
    Object.keys(map).forEach(function (id) {
      var v = map[id];
      if (!(v.revenue > 0 || v.costs > 0 || v.maint > 0 || v.loan > 0)) return;
      v.net = v.revenue - v.costs - v.maint;
      v.days = vehicleDays[id] || 0;
      v.util = periodDays > 0 ? (v.days / periodDays * 100) : 0;
      v.profit = v.net;
      rows.push(v);
    });
    return rows;
  }

  // The drill-down's sort (bi.js renderPLDrilldown "Sort"): a STABLE sort on
  // one numeric column, so ties keep the order the view built its rows in.
  // dir 'desc' (default) or 'asc'. Returns a new array.
  function jcRank(rows, key, dir) {
    var asc = dir === 'asc';
    return (Array.isArray(rows) ? rows : []).slice().sort(function (a, b) {
      var av = a[key] != null ? a[key] : 0;
      var bv = b[key] != null ? b[key] : 0;
      return asc ? av - bv : bv - av;
    });
  }

  var api = Object.freeze({
    DIMS: JC_DIMS.slice(),
    UNASSIGNED: JC_UNASSIGNED,
    labor: JC_LABOR,
    laborRateCoreFactory: jcLaborRateCoreFactory,
    entityTotals: jcEntityTotals,
    profit: jcProfit,
    crewTotals: jcCrewTotals,
    vehicleTotals: jcVehicleTotals,
    withAllocationVehicle: jcWithAllocationVehicle,
    rank: jcRank
  });
  if (root) root.JobCost = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
